import { DialogProvider, HUB_PLATE_CONTROLS_CLASS, RootStoreProvider } from "@intrasync/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { observer } from "mobx-react";
import App from "./App";
import type RootStore from "./stores/RootStore";
import { syncThemeColorMeta } from "./themeColorMeta";
import ResponsiveNotificationProvider from "./ResponsiveNotificationProvider";
import type { RootStore as LibraryRootStore } from "@intrasync/stores";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider, alpha, createTheme, type Theme } from "@mui/material/styles";

/**
 * App-wide RESPONSIVE overrides layered on top of the ThemeStore theme. These
 * are the systemic mobile/tablet fixes — applied once, globally, instead of in
 * every view:
 *
 *  - The route header's control strip (see `PLATE_CONTROLS` below — a global
 *    style, not a theme override, because since @intrasync/react 31 the header
 *    is no longer a MUI component). It stays one row and scrolls horizontally;
 *    its controls never shrink into slivers.
 *  - MuiTableContainer: a table's scroll container is almost always a flex item,
 *    whose default `min-width:auto` lets it grow to the table's intrinsic width
 *    and widen the whole page. Pinning `min-width:0` + `max-width:100%` makes the
 *    container shrink to the viewport so the table scrolls *inside* it.
 */

/**
 * Every route header in the app is the same DOM, and since @intrasync/react 31
 * (HQ 26.9.26) it is NOT a MUI component. `AppLayout.RoutePlate` portals the
 * route's header into `div#HubPlate` through `HubLayout/HubPlate`'s
 * `PlateContent`: title group, divider, then ONE control container —
 * `display:flex; flex:1 1 auto; min-width:0; overflow-x:auto` — holding the
 * header component's children, then the plate's refresh button.
 *
 * These rules used to hang off `MuiToolbar.styleOverrides.dense`, which reached
 * the old `CardHeader` strip. After the 31 upgrade no route header contains a
 * dense toolbar, so every rule below was silently dead: the style audit of
 * 2026-09-28 measured 37 of 65 headers squeezing a field to 0-45px at 390px.
 * The container already scrolls; what it lacked was the children refusing to
 * shrink first — MUI's FormControl carries `min-width: 0`, so a search field
 * gave up its whole width before anything overflowed.
 *
 * Since @intrasync/react 31.0.5 the container carries the class
 * `IntraSyncPlateControls` (`HUB_PLATE_CONTROLS_CLASS`), and that is the hook.
 * Until then it was found by STRUCTURE (the element right after the plate's
 * `aria-hidden` divider, which a header with no title group does not render,
 * plus inline-style fallbacks). Matching inline styles is a trap: React writes
 * `overflowX` + `overflowY` and the browser serialises them as the SHORTHAND
 * (`overflow: auto hidden`), so a `[style*="overflow-x"]` selector alone
 * matches nothing (measured 2026-09-29: the first attempt at this fix was
 * inert for exactly that reason). It applies at EVERY width: the plate is the
 * same element on desktop, and a 1000px window squeezed "New Client" out of
 * view too.
 *
 * What made the old strip unreliable, and still holds here:
 *
 *  1. `scrollbar-width: thin` reserves a real ~11px gutter INSIDE a fixed-height
 *     strip, and the 41px inputs then get clipped from the bottom by the
 *     `overflow-y: hidden` that horizontal scrolling requires. Phones paint
 *     overlay scrollbars anyway, so the gutter bought nothing.
 *  2. Nothing told the user there was more to the right. A 3px webkit scrollbar
 *     is the honest cue: browsers only paint it when the strip overflows.
 */
const PLATE_CONTROLS = `#HubPlate > .${HUB_PLATE_CONTROLS_CLASS}`;

const headerScrollStrip = (theme: Theme) => ({
  flexWrap: "nowrap" as const,
  // Firefox has no height control for `thin`, and 11px out of a 45px strip
  // clips the controls — take the cue loss over the clipping.
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": { height: 3, WebkitAppearance: "none" },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  // From the theme, not white: the strip sits on the plate's
  // `background.paper`, which is white in light mode.
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.text.primary, 0.35),
    borderRadius: 3,
  },
  // A horizontal swipe on the strip must not chain into the page (or the
  // browser's back gesture) once it reaches the end.
  overscrollBehaviorX: "contain" as const,
  WebkitOverflowScrolling: "touch" as const,
  // The strip scrolls; children must NOT flex-shrink to
  // min-content first (TextFields collapse into slivers otherwise).
  "& > *": { flexShrink: 0 },
  // H16 · A `flex: 1 1 0` field in a `nowrap` strip that is ALREADY overflowing
  // resolves to basis 0 with no free space to grow into — measured at 6px on
  // User Management, 28px on Invoices, 51px on Recurring. The control that was
  // supposed to yield gracefully instead vanished, and the toolbar "fit"
  // because the search box had ceased to exist. Nothing in this strip may be
  // narrower than a field you can actually see a word in.
  "& .MuiFormControl-root, & .MuiTextField-root": { minWidth: 120 },
  // Multi-word buttons stay on one line and scroll into view
  // instead of wrapping into a clipped two-line square.
  "& .MuiButton-root": { whiteSpace: "nowrap" },
  // H11 · A floating `InputLabel` is positioned OUTSIDE its 41px control, and
  // the strip clips vertically (`overflow-y: hidden`) — so the label renders
  // outside the plate, orphaned from its field and sliced by the app bar. Seen on Registry Images, Audit Log, API Keys and Integration
  // Errors. Inside this strip a select names itself with `displayEmpty` +
  // `renderValue`, never with a floating label. The notch goes with it —
  // leaving it would cut a labelled gap in an outline with no label in it.
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
});

function withResponsiveOverrides(base: Theme): Theme {
  return createTheme(base, {
    components: {
      MuiTableContainer: {
        styleOverrides: {
          root: { minWidth: 0, maxWidth: "100%" },
        },
      },
      // Denser cells on phones: wide list tables scroll inside their
      // container, so tighter padding means meaningfully more data per screen.
      MuiTableCell: {
        styleOverrides: {
          root: {
            "@media (max-width:599.95px)": {
              padding: "6px 8px",
            },
          },
        },
      },
      // H7 · The 44px touch floor, applied where a finger is actually the
      // input device (below `md` — phone and iPad portrait) rather than
      // everywhere, so desktop density is untouched.
      //
      // The audit measured the same handful of sizes over and over: 30.75px
      // "Unlock" next to a locked account, 30px Revoke/Delete pairs on API
      // Keys, ~31px header write actions, and — the smallest target found —
      // the sort labels in `TableSort/SortableHeader` at ~20px, with **29
      // consumers**, which is why this belongs in the theme and not in 29
      // files.
      MuiIconButton: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": { minHeight: 44 },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": {
              minHeight: 44,
              alignItems: "center",
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": { minHeight: 44 },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": { padding: 10 },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": { minWidth: 44, minHeight: 44 },
          },
        },
      },
      // H12 (input half) · iOS zooms a focused input whose text is under 16px
      // and does NOT zoom back out — pinch-zoom was deliberately restored in
      // 54d36f1, so the user is left at 1.3x with the layout off-screen. Every
      // `size="small"` TextField in the app is 14px, which is the entire
      // Delivery Connections form, the login fields, and every header search.
      // 16px is the floor on touch widths only; desktop keeps its density.
      MuiInputBase: {
        styleOverrides: {
          input: {
            "@media (max-width:899.95px)": { fontSize: 16 },
          },
        },
      },
      // L14 stop-gap · `CompanyIndicator` hard-codes `minWidth: 180`, which
      // overflows the app bar at 427px on every route with
      // `showCompanySelector`. It is a direct child of the app-bar Toolbar
      // (library `AppLayout.Toolbar`), so it can be capped precisely from here.
      //
      // Scoped to `MuiAppBar` on purpose: the route header strip is a
      // `MuiToolbar` too, but it lives inside the card and NOT inside an
      // AppBar, so this cannot reach the header selects that H16 above just
      // gave a 120px floor to.
      MuiAppBar: {
        styleOverrides: {
          root: {
            "@media (max-width:899.95px)": {
              "& .MuiToolbar-root > .MuiFormControl-root": {
                minWidth: 0,
                maxWidth: 132,
              },
            },
          },
        },
      },
    },
  });
}

// Observer wrapper so theme changes from ThemeStore (which can be driven
// by user preferences, system prefers-color-scheme, or the UserPreferences
// view) propagate through MUI's ThemeProvider reactively.
const ReactiveThemeProvider = observer(
  ({
    rootStore,
    children,
  }: {
    rootStore: RootStore;
    children: React.ReactNode;
  }) => {
    const theme = React.useMemo(
      () => withResponsiveOverrides(rootStore.themeStore.currentTheme),
      [rootStore.themeStore.currentTheme]
    );
    // The phone's status bar (and an installed PWA's title bar) takes the
    // page's `theme-color`. Keep it the colour the library AppLayout paints its
    // app bar — `palette.primary.main`, as HQ passes no `chromeColor` — so it
    // follows a user's colour scheme instead of staying the static default in
    // index.html / pwaManifest.ts.
    const chrome = theme.palette.primary.main;
    React.useEffect(() => {
      syncThemeColorMeta(chrome);
    }, [chrome]);
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles styles={(t) => ({ [PLATE_CONTROLS]: headerScrollStrip(t as Theme) })} />
        {children}
      </ThemeProvider>
    );
  }
);

export function renderApp(rootStore: RootStore): void {
  const container = document.getElementById("root");
  if (container) {
    const root = createRoot(container);
    root.render(
      <React.StrictMode>
        {/* HQ runs its own RootStore (src/stores/RootStore.ts), a structural fork of
            the library class. Since @intrasync/react 31 the provider is typed against
            the real @intrasync/stores RootStore, which HQ's fork does not implement in
            full (allStoresReady, instanceId, …). No library code reads
            those unguarded — the same was true on 26.9.1 — so this cast changes no
            runtime behaviour; it states the fork the old stub type hid. */}
        <RootStoreProvider rootStore={rootStore as unknown as LibraryRootStore}>
          <ReactiveThemeProvider rootStore={rootStore}>
            <DialogProvider>
              <ResponsiveNotificationProvider>
                <App />
              </ResponsiveNotificationProvider>
            </DialogProvider>
          </ReactiveThemeProvider>
        </RootStoreProvider>
      </React.StrictMode>
    );
  } else {
    console.error("Root container not found");
  }
}
